/**
 * **R9 §4.4 (D330) — the build floor: the one question the shell asks before it mounts anything.**
 *
 * `min_build_number` is compared against `store-build.json`'s monotonic counter (D261), which
 * `native/scripts/release.mjs` bakes into the bundle as `BUILD.storeBuild`. **An integer compares
 * with no parser**, which is the whole reason the floor is not a semver (R9 §12): there is no
 * comparator on this path to get wrong.
 *
 * Three of §4.4's four rules are this function, and each is a `false` rather than a `true`:
 *
 *  - **A build with no store build is never blocked.** `BUILD.storeBuild` is `undefined` on the
 *    PWA and on a sideloaded debug build. A browser client is never more than one deploy behind
 *    and D137's update prompt is its rail; a sideload has no store to update from, so a floor it
 *    could not act on would be a dead end rather than a block.
 *  - **A cold cache never blocks.** The floor is read from whatever document the store is running
 *    on, and a launch with no cache and no network is running the registry's compiled-in defaults,
 *    which carry no floor at all. §5's fail-open rule is stronger than the floor and outranks it
 *    wherever the two meet.
 *  - **A cached floor persists offline**, and that is the rule with a stated cost. The store seeds
 *    itself from `localStorage` synchronously at module load (D328), so a resident whose last
 *    fetched document set a floor above their build is still blocked with the radio off. 🔸 R9
 *    §4.4 holds this hardest: a blocked resident at the gate with no signal cannot open it, and
 *    the alternative — enforce only while online — is a floor airplane mode evades, which is not
 *    a floor. Do not "improve" this into an online-only check.
 *
 * **It reads the resolved document rather than re-parsing one.** `ResolvedClientConfig`
 * already carries `minBuildNumber`, where `undefined` is *no floor* and a wire value of `0` has
 * already landed there as `undefined` (`document.ts`). A second reader of the same field is a
 * second place for the *no floor* encoding to drift.
 *
 * **Called once, at the shell, before any screen** (`main.tsx`) — never on the open path, which
 * imports nothing from this directory at all (§5, and the two tests that hold it).
 */

import { BUILD } from '../pwa/version'
import { getClientConfig } from './store'

export function isBuildBelowFloor(): boolean {
  const build = BUILD.storeBuild
  if (build === undefined) return false
  const floor = getClientConfig().minBuildNumber
  if (floor === undefined) return false
  return build < floor
}
